import { Link } from "gatsby"
import { FC } from "react"
import classNames from "classnames"

// components
import { MicroCopy } from "../ContactForm/ContactForm"

// icons
import { Typography, ChevronLeftIcon, ChevronRightIcon } from "@eventbrite/marmalade"

// utils
import { renderRichText } from "../../utils/contentful/render_rich_text"
import { events } from "../../utils/heap/tracking_events"

// styles
import * as styles from "./Breadcrumbs.module.scss"
import { handleUrlString } from "../../utils/seo/seo_utils"

export const trackClick = (title: string, url: string) => {
  if (typeof window !== "undefined") {
    const baseUrl = window?.location?.href
    const finalUrl = baseUrl + url

    events?.breadcrumbClick(`${title} - ${finalUrl}`, Breadcrumbs?.name)
  }
}

export type Props = {
  contentfulMicrocopy: Queries.HelpArticleQuery["navigationHomepage"]
  prefixedBasePath: string
  nodeLocale: string
  topic?: string
  slug?: string
  className?: string
}

type BreadcrumbMicroCopy = {
  navigationHomepage: MicroCopy
}

const FALLBACK_MICROCOPY = {
  microcopyKey: "navigationHomepage",
  microcopyValue: {
    raw: '{"data":{},"content":[{"data":{},"content":[{"data":{},"marks":[],"value":"Help Center","nodeType":"text"}],"nodeType":"paragraph"}],"nodeType":"document"}'
  }
}

export const Breadcrumbs: FC<Props> = ({
  topic,
  slug,
  className,
  contentfulMicrocopy,
  prefixedBasePath,
  nodeLocale
}) => {
  const microCopy =
    (contentfulMicrocopy?.microcopyValue?.raw?.length ?? 0) > 0
      ? contentfulMicrocopy?.microcopyValue?.raw ?? undefined
      : FALLBACK_MICROCOPY?.microcopyValue?.raw ?? undefined

  return (
    <div className={classNames(styles.breadcrumbSection, className)}>
      {topic && slug ? (
        <div>
          <div
            className={classNames(
              styles?.innerSection,
              styles?.oneLevelContainer
            )}
          >
            <LeftArrowBreadCrumb
              topic={topic}
              slug={slug}
              prefixedBasePath={prefixedBasePath}
            />
          </div>

          <div
            className={classNames(
              styles?.innerSection,
              styles?.twoLevelContainer
            )}
          >
            <Link
              to={
                prefixedBasePath
                  ? handleUrlString(`${prefixedBasePath}/`, {
                      removePrefix: true
                    })
                  : handleUrlString("/", {
                      removePrefix: true
                    })
              }
              onClick={() => trackClick("Help Center", "/")}
            >
              {renderRichText(microCopy)}
            </Link>

            <div className={styles.breadcrumbIconContainer}>
              <ChevronRightIcon size="medium" />
            </div>

            <Link
              to={
                prefixedBasePath
                  ? handleUrlString(`${prefixedBasePath}/topics/${slug}/`, {
                      removePrefix: true
                    })
                  : handleUrlString(`/topics/${slug}/`, { removePrefix: true })
              }
              onClick={() => trackClick(topic, `/topics/${slug}`)}
            >
              <Typography variant="body-md" as="p" color="none">
                {topic}
              </Typography>
            </Link>
          </div>
        </div>
      ) : (
        <>
          <div
            className={classNames(
              styles.breadcrumbIconContainer,
              styles.leftChevronContainer
            )}
          >
            <ChevronLeftIcon size="medium" />
          </div>

          <Link
            to={`/${nodeLocale.toLowerCase()}/`}
            onClick={() => trackClick("Help Center", "/")}
          >
            {renderRichText(microCopy)}
          </Link>
        </>
      )}
    </div>
  )
}

export default Breadcrumbs

const LeftArrowBreadCrumb: FC<{
  topic: string
  slug: string
  prefixedBasePath: string
}> = ({ topic, prefixedBasePath, slug }) => {
  return (
    <>
      <div
        className={classNames(
          styles.breadcrumbIconContainer,
          styles.leftChevronContainer
        )}
      >
        <ChevronLeftIcon size="medium" />
      </div>

      <Link
        to={
          prefixedBasePath
            ? handleUrlString(`${prefixedBasePath}/topics/${slug}/`, {
                removePrefix: true
              })
            : handleUrlString(`/topics/${slug}/`, { removePrefix: true })
        }
        onClick={() => trackClick(topic, `/topics/${slug}`)}
      >
        {typeof topic === "string" ? (
          <Typography variant="body-md" as="p" color="none">
            {topic}
          </Typography>
        ) : (
          renderRichText(topic)
        )}
      </Link>
    </>
  )
}